import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

import {
  LedgerBadge,
  LedgerTable,
  type LedgerBadgeTone,
  type LedgerColumn,
} from '@/components/ui/LedgerTable'
import { AssignUserDialog } from '@/components/AssignUserDialog'
import { ChangeStatusDialog } from '@/components/ChangeStatusDialog'
import { ALL_SERVICE_STATUSES } from '@/db/domain/serviceStatusOrder'
import { statusIsBlocking, statusRequiresReason } from '@/db/domain/statusRules'
import { InlineNoteMarkdown, NoteMarkdown } from '@/components/NoteMarkdown'
import { PanelNoteCard, PanelNoteComposer } from '@/components/PanelNotes'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import { StatusLabelStacked } from '@/components/StatusLabelStacked'
import { type PhotoNoteSubmit } from '@/components/PhotoNoteComposer'
import { formatServiceStatus } from '@/help/serviceStatus'
import { formatBookedLabel } from '@/lib/datetime'
import { initials } from '@/lib/initials'
import {
  differingOccurredLabel,
  formatRelativeDateTime,
  formatShortRelativeDateTime,
} from '@/lib/relativeDateTime'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import type { ServiceStatus, StatusChangeLog } from '@/lib/schemas/db'

export type ServiceType = 'callout' | 'remote' | 'sitdown' | 'job'

export interface ServiceActivityEntry {
  id: string
  kind: 'create' | 'status' | 'block' | 'invoice' | 'note' | 'edit'
  /** UTC ISO timestamp — when this was recorded. Orders the feed. */
  createdAt: string
  /** When a status change really happened, if that differs from the above. */
  occurredAt?: string
  who: string
  /** Event line, e.g. 'Status → Blocked' (events only) */
  action?: string
  reason?: string
  /** Note text (notes only) */
  text?: string
  photoUrl?: string
  /** Every photo a photo note currently shows. */
  photoUrls?: string[]
  /** The underlying note's id, for edits (notes only). */
  noteId?: string
  /** True when the entry is a photo note, so an edit patches the caption. */
  isPhoto?: boolean
  /** Set when the note was soft-deleted; the card renders as a tombstone. */
  noteDeleted?: { at: string; byName?: string }
}

export interface ServiceItem {
  /** Unique across service types, e.g. 'job:abc123' */
  key: string
  type: ServiceType
  /** Public-facing ref */
  id: string
  privateId: string
  customerName: string
  customerId?: string
  customerPublicId?: string
  summary: string
  /** ZA-local 'YYYY-MM-DD' */
  bookedDate: string
  /** ZA-local 'HH:mm' or '' for whole-day / jobs */
  bookedTime: string
  /** Sortable UTC ISO timestamp */
  bookedSort: string
  status: ServiceStatus
  statusChanges: StatusChangeLog[]
  statusTransitions: ServiceStatus[]
  blockedReason?: string
  unsuccessfulReason?: string
  /** Physical shelf tag for jobs; other service types have none. */
  numberTag?: string
  assignedUserId?: string
  assignedUserName?: string
  phone?: string
  updatedAt?: string
  activity: ServiceActivityEntry[]
  detailsTo: '/callouts/$id' | '/sessions/$id' | '/jobs/$id'
}

export interface ServicesScreenProps {
  services: ServiceItem[]
  users?: Array<{ id: string; name: string }>
  onAddNote?: (item: ServiceItem, text: string) => void | Promise<void>
  /** Add a photo note (cropped + compressed) to a service. */
  onAddPhoto?: (
    item: ServiceItem,
    input: PhotoNoteSubmit,
  ) => void | Promise<void>
  /** Save an edited note body — text, or a photo note's caption. */
  onEditNote?: (
    item: ServiceItem,
    noteId: string,
    patch: { text?: string; caption?: string },
  ) => void | Promise<void>
  /** Soft-delete a note (kept as a tombstone in the record). */
  onDeleteNote?: (item: ServiceItem, noteId: string) => void | Promise<void>
  onChangeStatus?: (
    item: ServiceItem,
    status: ServiceStatus,
    reason: string,
    /** UTC ISO when the event really happened; undefined = it happened now. */
    occurredAt?: string,
  ) => void | Promise<void>
  onAssign?: (item: ServiceItem, userId: string) => void | Promise<void>
  getAuthorDisplayName?: (authorId: string) => string
  title?: string
  description?: string
  emptyLabel?: string
  /**
   * Render without the standalone-page chrome (own <main>, background,
   * page margins) so a host screen can mount the ledger + side panel
   * inside its own layout (e.g. the kiosk-control check-out selector).
   */
  embedded?: boolean
  /** Extra ledger columns appended after the standard ones (e.g. a per-row action). */
  extraColumns?: LedgerColumn<ServiceItem>[]
  /** Standard columns to drop, by key — for hosts where a column is constant (e.g. 'type' when every row is a job). */
  hideColumns?: string[]
  /** Extra header actions, passed through to the ledger (e.g. a Back button). */
  actions?: ReactNode
  /**
   * Auto-select the first row so the side panel is populated on load
   * (the services page default). Pass false to keep the panel closed until
   * a row is clicked.
   */
  autoSelectFirst?: boolean
}

export const serviceTypeMeta: Record<
  ServiceType,
  { label: string; tone: LedgerBadgeTone; accentClass: string }
> = {
  callout: { label: 'Callout', tone: 'blue', accentClass: 'shadow-[inset_3px_0_0_#3b82f6]' },
  remote: { label: 'Remote', tone: 'violet', accentClass: 'shadow-[inset_3px_0_0_#8b5cf6]' },
  sitdown: { label: 'Sit-down', tone: 'teal', accentClass: 'shadow-[inset_3px_0_0_#14b8a6]' },
  job: { label: 'Job', tone: 'amber', accentClass: 'shadow-[inset_3px_0_0_#f59e0b]' },
}

const activityDotClass: Record<ServiceActivityEntry['kind'], string> = {
  create: 'bg-slate-400',
  status: 'bg-blue-500',
  note: 'bg-amber-500',
  edit: 'bg-slate-500',
  invoice: 'bg-emerald-500',
  block: 'bg-rose-500',
}


/**
 * The shared unified-ledger column set (Ref, Type, Customer, Issue, Booked,
 * Assignee, Status, Updated) — used by the Services and Archived pages.
 *
 * Takes no rows: every column's filter options are now static, drawn from the
 * domain rather than from whatever happens to be loaded.
 */
export function buildServiceLedgerColumns(
  getAuthorDisplayName?: (authorId: string) => string,
): LedgerColumn<ServiceItem>[] {
  // Every status, not just the ones on screen. Deriving from the loaded rows
  // meant a status you wanted to filter *to* was missing precisely because
  // nothing was in it yet — the filter hid its own answer.
  const statusFilterOptions = ALL_SERVICE_STATUSES.map((status) => ({
    value: status,
    label: formatServiceStatus(status),
  }))

  return [
    {
      key: 'ref',
      label: 'Ref',
      widthClass: 'w-28',
      sortValue: (item) => item.id,
      filter: 'text',
      filterValue: (item) => item.id,
      render: (item) => (
        <Link
          to={item.detailsTo}
          params={{ id: item.id }}
          title="Open details page"
          className="font-mono text-[12.5px] whitespace-nowrap text-blue-700 hover:underline"
        >
          {item.id}
        </Link>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      widthClass: 'w-24',
      sortValue: (item) => item.type,
      filter: 'select',
      filterOptions: (['callout', 'remote', 'sitdown', 'job'] as const).map(
        (type) => ({ value: type, label: serviceTypeMeta[type].label }),
      ),
      filterValue: (item) => item.type,
      render: (item) => (
        <LedgerBadge tone={serviceTypeMeta[item.type].tone}>
          {serviceTypeMeta[item.type].label}
        </LedgerBadge>
      ),
    },
    {
      key: 'tag',
      label: 'Tag',
      widthClass: 'w-20',
      sortValue: (item) => item.numberTag ?? '',
      filter: 'text',
      filterValue: (item) => item.numberTag ?? '',
      cellClass: 'whitespace-nowrap',
      render: (item) =>
        item.numberTag ? (
          <span className="font-mono text-[12.5px] text-slate-700">
            {item.numberTag}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      key: 'customer',
      label: 'Customer',
      widthClass: 'w-44',
      sortValue: (item) => item.customerName.toLowerCase(),
      filter: 'text',
      filterValue: (item) => item.customerName,
      render: (item) => (
        <div className="overflow-hidden font-medium text-ellipsis whitespace-nowrap">
          {item.customerId ? (
            <Link
              to="/customers/$id"
              params={{ id: item.customerPublicId ?? item.customerId }}
              className="border-b border-blue-200 text-blue-700 hover:border-blue-700 hover:text-blue-800"
            >
              {item.customerName}
            </Link>
          ) : (
            item.customerName
          )}
        </div>
      ),
    },
    {
      key: 'issue',
      label: 'Issue',
      filter: 'text',
      filterValue: (item) => item.summary,
      cellClass: 'text-slate-600',
      render: (item) => (
        <div>
          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
            {item.summary}
          </div>
          {item.blockedReason ? (
            <div className="mt-1 flex items-start gap-1.5 text-[12.5px] leading-snug whitespace-normal text-rose-700">
              <span className="shrink-0">⚠</span>
              <span>
                <InlineNoteMarkdown text={item.blockedReason} />
              </span>
            </div>
          ) : null}
          {item.unsuccessfulReason ? (
            <div className="mt-1 flex items-start gap-1.5 text-[12.5px] leading-snug whitespace-normal text-orange-700">
              <span className="shrink-0 font-semibold">✗</span>
              <span>
                <InlineNoteMarkdown text={item.unsuccessfulReason} />
              </span>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'booked',
      label: 'Booked',
      widthClass: 'w-32',
      sortValue: (item) => item.bookedSort,
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (item) => formatBookedLabel(item.bookedDate, item.bookedTime),
    },
    {
      key: 'assignee',
      label: 'Assignee',
      widthClass: 'w-28',
      sortValue: (item) => (item.assignedUserName ?? '').toLowerCase(),
      filter: 'text',
      filterValue: (item) => item.assignedUserName ?? '',
      cellClass: 'whitespace-nowrap text-slate-600',
      render: (item) =>
        item.assignedUserName ?? <span className="text-slate-400">—</span>,
    },
    {
      key: 'status',
      label: 'Status',
      widthClass: 'w-40',
      sortValue: (item) => item.status,
      filter: 'select',
      filterOptions: statusFilterOptions,
      filterValue: (item) => item.status,
      cellClass: 'whitespace-nowrap',
      render: (item) => (
        <StatusHistoryPill
          variant="ledger"
          currentStatus={item.status}
          statusChanges={item.statusChanges}
          getAuthorDisplayName={getAuthorDisplayName}
        />
      ),
    },
    {
      key: 'updated',
      label: 'Updated',
      widthClass: 'w-24',
      sortValue: (item) => item.updatedAt ?? '',
      defaultSortDir: 'desc',
      cellClass: 'whitespace-nowrap text-[12.5px] text-slate-400',
      render: (item) =>
        item.updatedAt ? formatShortRelativeDateTime(item.updatedAt) : '—',
    },
  ]
}

export function ServicesScreen({
  services,
  users = [],
  onAddNote,
  onAddPhoto,
  onEditNote,
  onDeleteNote,
  onChangeStatus,
  onAssign,
  getAuthorDisplayName,
  title = 'Services',
  description = 'Every callout, remote session, sit-down and job in one ledger.',
  emptyLabel = 'No services have been captured yet.',
  embedded = false,
  extraColumns,
  hideColumns,
  actions,
  autoSelectFirst = true,
}: ServicesScreenProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  const [pendingMove, setPendingMove] = useState<ServiceItem | null>(null)
  const [pendingAssign, setPendingAssign] = useState<ServiceItem | null>(null)

  const selected =
    services.find((item) => item.key === selectedKey) ??
    (autoSelectFirst ? (services[0] ?? null) : null)

  // The feed reads oldest→newest, so land the scroll on the newest entries —
  // on selection change and whenever something is added.
  const activityScrollRef = useRef<HTMLDivElement | null>(null)
  const activityCount = selected?.activity.length ?? 0
  useEffect(() => {
    const el = activityScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [selected?.key, activityCount])

  const columns = [
    ...buildServiceLedgerColumns(getAuthorDisplayName).filter(
      (column) => !hideColumns?.includes(column.key),
    ),
    ...(extraColumns ?? []),
  ]

  // Embedded mode leaves the <main> landmark, background and page margins to
  // the host screen.
  const Shell: 'main' | 'div' = embedded ? 'div' : 'main'

  return (
    <div className={embedded ? 'text-slate-900' : 'bg-slate-50 text-slate-900'}>
      <Shell
        className={
          embedded
            ? 'flex items-start gap-4'
            : 'mx-auto flex max-w-[1720px] items-start gap-4 px-4 py-6'
        }
      >
        <div className="min-w-0 flex-1">
          <LedgerTable
            rows={services}
            columns={columns}
            rowKey={(item) => item.key}
            itemLabelPlural="services"
            emptyLabel={emptyLabel}
            title={title}
            description={description}
            actions={actions}
            onRowClick={(item) => {
              setSelectedKey(item.key)
              // Selecting a row is an explicit ask to see its details —
              // re-expand the panel if it was collapsed.
              setPanelCollapsed(false)
            }}
            rowHighlight={(item) =>
              selected && item.key === selected.key
                ? `bg-blue-50 ${serviceTypeMeta[item.type].accentClass}`
                : undefined
            }
            defaultSort={{ key: 'updated', dir: 'desc' }}
            paginated={false}
            minTableWidthClass="min-w-[920px]"
            mobileFilterKeys={['assignee', 'status']}
            mobileCard={(item) => (
              <div
                className={`space-y-1.5 px-3.5 py-3 ${serviceTypeMeta[item.type].accentClass}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="font-mono text-[12px] text-slate-500">
                      {item.id}
                    </span>
                    <LedgerBadge tone={serviceTypeMeta[item.type].tone}>
                      {serviceTypeMeta[item.type].label}
                    </LedgerBadge>
                  </div>
                  <span className="shrink-0 text-[12px] text-slate-400">
                    {item.updatedAt
                      ? formatShortRelativeDateTime(item.updatedAt)
                      : ''}
                  </span>
                </div>

                <Link
                  to={item.detailsTo}
                  params={{ id: item.id }}
                  className="block min-w-0"
                >
                  <div className="truncate font-medium text-slate-900">
                    {item.customerName}
                  </div>
                  <div className="line-clamp-2 text-[13px] leading-snug text-slate-600">
                    {item.summary}
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-500">
                    Booked {formatBookedLabel(item.bookedDate, item.bookedTime)}
                  </div>
                </Link>

                {item.blockedReason ? (
                  <div className="flex items-start gap-1.5 text-[12.5px] leading-snug text-rose-700">
                    <span className="shrink-0">⚠</span>
                    <span>
                      <InlineNoteMarkdown text={item.blockedReason} />
                    </span>
                  </div>
                ) : null}
                {item.unsuccessfulReason ? (
                  <div className="flex items-start gap-1.5 text-[12.5px] leading-snug text-orange-700">
                    <span className="shrink-0 font-semibold">✗</span>
                    <span>
                      <InlineNoteMarkdown text={item.unsuccessfulReason} />
                    </span>
                  </div>
                ) : null}

                <div className="flex items-center justify-between gap-2 pt-0.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <StatusHistoryPill
                      variant="ledger"
                      currentStatus={item.status}
                      statusChanges={item.statusChanges}
                      getAuthorDisplayName={getAuthorDisplayName}
                    />
                    {item.assignedUserName ? (
                      <span className="flex min-w-0 items-center gap-1">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10.5px] font-semibold text-slate-600">
                          {initials(item.assignedUserName)}
                        </span>
                        <span className="truncate text-[12px] text-slate-600">
                          {item.assignedUserName}
                        </span>
                      </span>
                    ) : (
                      <span className="text-[12px] text-slate-400">
                        Unassigned
                      </span>
                    )}
                  </div>
                  {onChangeStatus && item.statusTransitions.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setPendingMove(item)}
                      className="h-8 shrink-0 cursor-pointer rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 active:bg-slate-100"
                    >
                      Move…
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          />
        </div>

        {selected && panelCollapsed ? (
          <div className="sticky top-4 hidden w-[46px] shrink-0 flex-col items-center gap-3 self-start rounded-lg border border-slate-200 bg-white pt-3 pb-4 lg:flex">
            <button
              type="button"
              onClick={() => setPanelCollapsed(false)}
              title="Expand details"
              className="h-[30px] w-[30px] cursor-pointer rounded-lg border border-slate-300 bg-white text-[15px] leading-none text-slate-700"
            >
              ‹
            </button>
            <div className="max-h-[60vh] overflow-hidden text-[11px] tracking-wide whitespace-nowrap text-ellipsis text-slate-500 [writing-mode:vertical-rl]">
              {selected.id} · {selected.customerName}
            </div>
          </div>
        ) : null}

        {selected && !panelCollapsed ? (
          // Fixed height (not max-h) so the flex-1 activity body always
          // stretches to fill the leftover space — the other sections are
          // shrink-0 and keep their natural heights.
          <aside className="sticky top-4 hidden h-[calc(100dvh-110px)] w-96 shrink-0 flex-col self-start overflow-hidden rounded-lg border border-slate-200 bg-white lg:flex">
            {/* Header */}
            <div className="shrink-0 border-b border-slate-100 px-5 pt-[18px] pb-3.5">
              <div className="flex items-start justify-between gap-2.5">
                <div className="min-w-0">
                  <div className="mb-2 flex items-center gap-2">
                    <Link
                      to={selected.detailsTo}
                      params={{ id: selected.id }}
                      className="font-mono text-xs font-medium text-blue-700 hover:underline"
                    >
                      {selected.id}
                    </Link>
                    <LedgerBadge tone={serviceTypeMeta[selected.type].tone}>
                      {serviceTypeMeta[selected.type].label}
                    </LedgerBadge>
                  </div>
                  <h2 className="m-0 text-base font-semibold">
                    {selected.customerName}
                  </h2>
                  <NoteMarkdown
                    text={selected.summary}
                    className="text-[12.5px] leading-snug text-slate-500"
                  />
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Link
                    to={selected.detailsTo}
                    params={{ id: selected.id }}
                    title="Open service page"
                    className="inline-flex h-7 items-center gap-1 rounded-[7px] border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Open
                    <span aria-hidden className="text-[10px]">
                      ↗
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setPanelCollapsed(true)}
                    title="Collapse details"
                    className="h-7 w-7 cursor-pointer rounded-[7px] border border-slate-200 bg-white text-sm leading-none text-slate-500"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>

            {/* Status + assignee */}
            <div className="flex shrink-0 flex-col gap-2.5 border-b border-slate-100 px-5 py-3.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${getServiceStatusMeta(selected.status).dotClass}`}
                  />
                  <span
                    className={`text-[13px] font-semibold ${getServiceStatusMeta(selected.status).textClass}`}
                  >
                    <StatusLabelStacked status={selected.status} />
                  </span>
                </div>
                {onChangeStatus && selected.statusTransitions.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setPendingMove(selected)}
                    className="h-[30px] cursor-pointer rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Change status…
                  </button>
                ) : null}
              </div>
              {onAssign ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-[10px] tracking-wider text-slate-400 uppercase">
                      Assigned
                    </span>
                    <span
                      className={`truncate text-[12.5px] ${selected.assignedUserName ? 'text-slate-900' : 'text-slate-400'}`}
                    >
                      {selected.assignedUserName ?? 'Unassigned'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingAssign(selected)}
                    className="h-[30px] shrink-0 cursor-pointer rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {selected.assignedUserName ? 'Reassign…' : 'Assign…'}
                  </button>
                </div>
              ) : null}
              {statusIsBlocking(selected.status) && selected.blockedReason ? (
                <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                  <span className="shrink-0 text-sm leading-tight text-rose-600">
                    ⚠
                  </span>
                  <div>
                    {/* The real status, so a parts stall doesn't read as a
                        generic block the office has to open to understand. */}
                    <div className="mb-0.5 text-[10px] font-semibold tracking-wider text-rose-600 uppercase">
                      {formatServiceStatus(selected.status)}
                    </div>
                    <NoteMarkdown
                      text={selected.blockedReason}
                      className="text-[12.5px] leading-relaxed text-rose-700"
                    />
                  </div>
                </div>
              ) : null}
              {selected.status === 'ReadyForCollectionUnsuccessful' &&
              selected.unsuccessfulReason ? (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                  <span className="shrink-0 text-sm font-semibold leading-tight text-red-600">
                    ✗
                  </span>
                  <div>
                    <div className="mb-0.5 text-[10px] font-semibold tracking-wider text-red-600 uppercase">
                      Unsuccessful
                    </div>
                    <NoteMarkdown
                      text={selected.unsuccessfulReason}
                      className="text-[12.5px] leading-relaxed text-red-700"
                    />
                  </div>
                </div>
              ) : null}
            </div>

            {/* Quick facts */}
            <div className="grid shrink-0 grid-cols-2 gap-x-4 gap-y-3 border-b border-slate-100 px-5 py-3.5">
              <div>
                <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                  {selected.type === 'job' ? 'Checked in' : 'Booked'}
                </div>
                <div className="text-[12.5px] text-slate-900">
                  {formatBookedLabel(selected.bookedDate, selected.bookedTime)}
                </div>
              </div>
              <div>
                <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                  Contact
                </div>
                <div className="text-[12.5px] text-slate-900">
                  {selected.phone || '—'}
                </div>
              </div>
            </div>

            {/* Activity */}
            <div className="flex shrink-0 items-center justify-between px-5 pt-3.5 pb-2">
              <h3 className="m-0 text-[11px] font-semibold tracking-wider text-slate-600 uppercase">
                Activity
              </h3>
              <span className="text-[11px] text-slate-400">
                {selected.activity.length}{' '}
                {selected.activity.length === 1 ? 'entry' : 'entries'}
              </span>
            </div>

            <div
              ref={activityScrollRef}
              className="min-h-24 flex-1 overflow-auto px-5 pb-4"
            >
              <div className="overflow-hidden rounded-lg border border-slate-200">
                {selected.activity.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-400">
                    No activity yet.
                  </div>
                ) : null}
                {selected.activity.map((entry, index) =>
                  entry.kind === 'note' ? (
                    <PanelNoteCard
                      key={entry.id}
                      note={{
                        id: entry.id,
                        authorName: entry.who,
                        createdAt: entry.createdAt,
                        text: entry.text,
                        photoUrl: entry.photoUrl,
                        photoUrls: entry.photoUrls,
                        deleted: entry.noteDeleted,
                      }}
                      onSaveEdit={
                        onEditNote && entry.noteId && !entry.noteDeleted
                          ? (next) =>
                              void onEditNote(
                                selected,
                                entry.noteId as string,
                                entry.isPhoto
                                  ? { caption: next }
                                  : { text: next },
                              )
                          : undefined
                      }
                      onDelete={
                        onDeleteNote && entry.noteId && !entry.noteDeleted
                          ? () =>
                              void onDeleteNote(
                                selected,
                                entry.noteId as string,
                              )
                          : undefined
                      }
                    />
                  ) : (
                    <div
                      key={entry.id}
                      className={`grid grid-cols-[56px_1fr] gap-2.5 border-b border-slate-100 px-2.5 py-2 last:border-b-0 ${index % 2 === 1 ? 'bg-slate-50' : 'bg-white'}`}
                    >
                      <div
                        className="font-mono text-[11px] leading-normal text-slate-400"
                        title={formatRelativeDateTime(entry.createdAt)}
                      >
                        {formatShortRelativeDateTime(entry.createdAt)}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-1.5">
                          <span
                            className={`h-1.5 w-1.5 shrink-0 -translate-y-px rounded-full ${activityDotClass[entry.kind]}`}
                          />
                          <span className="text-xs leading-snug text-slate-900">
                            {entry.action}
                          </span>
                        </div>
                        {/* The row is stamped with when it was logged; say when
                            it occurred only when that reads differently. */}
                        {differingOccurredLabel(
                          entry.occurredAt,
                          entry.createdAt,
                        ) ? (
                          <div className="mt-0.5 pl-3 text-[11px] text-slate-500">
                            Occurred{' '}
                            {differingOccurredLabel(
                              entry.occurredAt,
                              entry.createdAt,
                            )}
                          </div>
                        ) : null}
                        {entry.reason ? (
                          entry.kind === 'block' ? (
                            <div className="mt-0.5 flex items-start gap-1 pl-3 text-[11.5px] leading-snug whitespace-normal text-rose-700">
                              <span className="shrink-0">⚠</span>
                              <NoteMarkdown text={entry.reason} />
                            </div>
                          ) : (
                            <NoteMarkdown
                              text={entry.reason}
                              className="mt-0.5 pl-3 text-[11.5px] leading-snug whitespace-normal text-slate-500"
                            />
                          )
                        ) : null}
                        <div className="mt-0.5 pl-3 text-[11px] text-slate-400">
                          {entry.who}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {onAddNote || onAddPhoto ? (
              <PanelNoteComposer
                className="border-t border-slate-100 px-5 py-3"
                onAddNote={
                  onAddNote ? (text) => onAddNote(selected, text) : undefined
                }
                onSubmitPhoto={
                  onAddPhoto
                    ? (input) => void onAddPhoto(selected, input)
                    : undefined
                }
              />
            ) : null}
          </aside>
        ) : null}
      </Shell>

      {/* Friction modals: deliberate status change / assignment */}
      {pendingMove ? (
        <ChangeStatusDialog
          open
          refLabel={pendingMove.id}
          contextLabel={pendingMove.customerName}
          currentStatus={pendingMove.status}
          options={pendingMove.statusTransitions}
          requiresReason={statusRequiresReason}
          onClose={() => setPendingMove(null)}
          onConfirm={async (status, reason, occurredAt) => {
            await onChangeStatus?.(pendingMove, status, reason, occurredAt)
          }}
        />
      ) : null}
      {pendingAssign ? (
        <AssignUserDialog
          open
          refLabel={pendingAssign.id}
          contextLabel={pendingAssign.customerName}
          users={users}
          currentUserId={pendingAssign.assignedUserId}
          onClose={() => setPendingAssign(null)}
          onConfirm={async (userId) => {
            await onAssign?.(pendingAssign, userId)
          }}
        />
      ) : null}
    </div>
  )
}
